import React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import MessagingInteractionImg from 'assets/images/img_messaging-interaction.r.svg'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { IconButton } from 'components/IconButton'
import { Form, FormGroupContainer } from 'components/inputs'
import { ModalSheet } from 'components/ModalSheet'
import { ConsultationIncompleteReason } from 'jobs/consultations/Consultation'
import IncompleteConsultationCompletionDialogViewModel from './IncompleteConsultationCompletionDialogViewModel'
import styles from './IncompleteConsultationCompletionDialog.sass'

interface IncompleteConsultationCompletionDialogProps {
  vm: IncompleteConsultationCompletionDialogViewModel
}

export default observer(function IncompleteConsultationCompletionDialog({
  vm,
}: IncompleteConsultationCompletionDialogProps) {
  return (
    <ModalSheet
      showing={vm.showing}
      disableCloseOnEsc
      disableCloseOnOutsideClick
      onRequestClose={() => vm.close()}
    >
      <div
        className={styles.modalContainer}
        data-test="incomplete-consultation-dialog"
      >
        <div className={styles.header}>
          <IconButton className={styles.close} onClick={() => vm.close()}>
            <Icon name="close" size={24} />
          </IconButton>
          <div className={styles.image}>
            <MessagingInteractionImg />
          </div>
        </div>
        <div className={styles.content}>
          <div className={styles.text}>
            <h1 className={styles.title}>
              Before finishing,
              <br />
              tell us why the call wasn't needed.
            </h1>
          </div>
          <div className={styles.radioContainer}>
            <Form id="incomplete-consultation-reason">
              <FormGroupContainer>
                <RadioButtonGroup>
                  <RadioButtonItem
                    active={
                      vm.reason ===
                      ConsultationIncompleteReason.ConsultationNotNeeded
                    }
                    dataTest="incomplete-consultation-dialog-reason-not-needed"
                    label="Consultation not needed"
                    onClick={() =>
                      vm.setReason(
                        ConsultationIncompleteReason.ConsultationNotNeeded,
                      )
                    }
                  />
                  <RadioButtonItem
                    active={
                      vm.reason ===
                      ConsultationIncompleteReason.ClientMissedCall
                    }
                    dataTest="incomplete-consultation-dialog-reason-client-missed-call"
                    label="Customer missed the call"
                    onClick={() =>
                      vm.setReason(
                        ConsultationIncompleteReason.ClientMissedCall,
                      )
                    }
                  />
                  <RadioButtonItem
                    active={
                      vm.reason === ConsultationIncompleteReason.ProMissedCall
                    }
                    dataTest="incomplete-consultation-dialog-reason-pro-missed-call"
                    label="I missed the call"
                    onClick={() =>
                      vm.setReason(ConsultationIncompleteReason.ProMissedCall)
                    }
                  />
                  <RadioButtonItem
                    active={
                      vm.reason ===
                      ConsultationIncompleteReason.PreviousProCompletedCall
                    }
                    dataTest="incomplete-consultation-dialog-reason-previous-pro-completed"
                    label="Previous Pro completed the call"
                    onClick={() =>
                      vm.setReason(
                        ConsultationIncompleteReason.PreviousProCompletedCall,
                      )
                    }
                  />
                  <RadioButtonItem
                    active={
                      vm.reason ===
                      ConsultationIncompleteReason.ConsultationHandledViaChat
                    }
                    dataTest="incomplete-consultation-dialog-reason-handled-via-chat"
                    label="Consultation done via chat"
                    onClick={() =>
                      vm.setReason(
                        ConsultationIncompleteReason.ConsultationHandledViaChat,
                      )
                    }
                  />
                  <RadioButtonItem
                    active={vm.reason === ConsultationIncompleteReason.Other}
                    dataTest="incomplete-consultation-dialog-reason-other"
                    label="Other"
                    onClick={() =>
                      vm.setReason(ConsultationIncompleteReason.Other)
                    }
                  >
                    {vm.reason === ConsultationIncompleteReason.Other && (
                      <textarea
                        className={styles.otherTextArea}
                        data-test="incomplete-consultation-dialog-other-textarea"
                        onChange={(e) => vm.setOtherText(e.target.value)}
                        rows={4}
                        value={vm.otherText}
                      />
                    )}
                  </RadioButtonItem>
                </RadioButtonGroup>
              </FormGroupContainer>
            </Form>
          </div>
        </div>
        <div className={styles.footer}>
          <div className={styles.buttonContainer}>
            <Button
              onClick={() => vm.close()}
              type="secondary"
              dataTest="incomplete-consultation-dialog-close-button"
            >
              Close
            </Button>
            <Button
              disabled={!vm.isValid}
              onClick={() => vm.submit()}
              type="primary"
              dataTest="incomplete-consultation-dialog-submit-button"
            >
              Submit & Complete Job
            </Button>
          </div>
          <div className={styles.infoText}>
            <Icon color="var(--grey600)" name="info" size={20} />
            <p className={styles.description}>
              Marking this job as complete will close communication with client.
            </p>
          </div>
        </div>
      </div>
    </ModalSheet>
  )
})

interface RadioButtonGroupProps {
  children: React.ReactNode
}

const RadioButtonGroup = observer(function RadioButtonGroup({
  children,
}: RadioButtonGroupProps) {
  return <div className={styles.radioButtonGroup}>{children}</div>
})

interface RadioButtonItemProps {
  active: boolean
  children?: React.ReactNode
  dataTest?: string
  label: string
  onClick: () => unknown
}

const RadioButtonItem = observer(function RadioButtonItem({
  active,
  children,
  dataTest,
  label,
  onClick,
}: RadioButtonItemProps) {
  const [hovering, setHovering] = React.useState(false)

  return (
    <div
      className={cx(styles.radioButtonItem, children && styles.withChildren)}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onClick={onClick}
      data-test={dataTest}
    >
      <Icon
        color={active ? 'var(--theme-base)' : 'var(--grey300)'}
        name={active || hovering ? 'radio-selected' : 'radio-blank'}
        size={24}
      />
      <div className={styles.radioButtonItemContent}>
        <label className={styles.radioButtonLabel}>{label}</label>
        {children}
      </div>
    </div>
  )
})
